import type { DrawingEntity, Point } from '../contract/types.js';
import { ARC_ANGLE_TOLERANCE_DEG } from './tolerance.js';
import { normaliseForComparison } from './normalise.js';
import { bulgeDisplacement, sampledPolyline, symmetricHausdorff } from './geometry.js';

/**
 * Whether a produced entity and a ground-truth entity may be considered the same.
 *
 * The rules are the board's, reproduced rather than proposed:
 *
 *   type match required, strictly one-to-one, a duplicate prediction is a false positive
 *   LINE          both endpoints within T; endpoint order ignored
 *   LWPOLYLINE    vertex-wise and Hausdorff distance within T
 *   CIRCLE        centre within T and radius difference within T
 *   ARC           centre and radius within T, and start and end angles within 0.5 degrees
 *   TEXT / MTEXT  normalised then matched exactly; anchor within T
 *
 * Returns the pairing cost when the two are candidates, or null when they are
 * not. The cost is what the assignment minimises; the null is what keeps an
 * impossible pairing out of the result entirely.
 */
export function candidateCost(
  produced: DrawingEntity,
  truth: DrawingEntity,
  tolerance: number,
  /**
   * The angular tolerance for arcs, defaulting to the board's 0.5 degrees.
   *
   * It is a parameter because half a degree is the right allowance for
   * *recognising* an arc out of a PDF and the wrong one for copying a number
   * from one file to another. Stage 2 of the accuracy pipeline compares a
   * drawing against the DXF written from it, where any difference at all is a
   * defect, and passes its own value here.
   */
  angleToleranceDeg: number = ARC_ANGLE_TOLERANCE_DEG,
): number | null {
  if (produced.type !== truth.type) return null;

  switch (produced.type) {
    case 'LINE': {
      const t = truth as typeof produced;
      // Endpoint order is ignored, so a line drawn in reverse still matches.
      const forward = Math.max(dist(produced.start, t.start), dist(produced.end, t.end));
      const reversed = Math.max(dist(produced.start, t.end), dist(produced.end, t.start));
      const cost = Math.min(forward, reversed);
      return cost <= tolerance ? cost : null;
    }

    case 'LWPOLYLINE': {
      const t = truth as typeof produced;
      // A closed shape and an open one with the same vertices are different
      // shapes, so this is not merely a property to compare loosely.
      if (produced.closed !== t.closed) return null;

      // Over the **sampled** shape, so a curved segment is compared as the curve
      // it is. Comparing the vertex lists made a rounded rectangle and a
      // chamfered one the same list of points.
      const hausdorff = symmetricHausdorff(
        sampledPolyline(produced.vertices, produced.bulges, produced.closed),
        sampledPolyline(t.vertices, t.bulges, t.closed),
        produced.closed,
      );
      if (hausdorff > tolerance) return null;

      if (produced.vertices.length !== t.vertices.length) {
        // Vertex counts differ, so only the Hausdorff distance is defined. This
        // is the fragmentation case the split and merge counts exist to surface.
        return hausdorff;
      }

      // Vertex-wise comparison is taken over every equivalent traversal, and the
      // bulges are carried through each of them.
      //
      // The board fixes endpoint-order insensitivity for LINE but says nothing
      // about polyline traversal. It has to be handled, because it is not a
      // tolerance question: a rectangle traversed clockwise from a different
      // starting corner is the *same rectangle*, and a PDF's winding direction
      // and starting vertex are the producer's choice, not the drawing's. See
      // docs/findings/07-polyline-traversal-is-unspecified.md.
      const best = bestTraversal(produced, t, tolerance);
      if (best === null) return null;
      return Math.max(best, hausdorff);
    }

    case 'CIRCLE': {
      const t = truth as typeof produced;
      const centre = dist(produced.centre, t.centre);
      const radius = Math.abs(produced.radius - t.radius);
      if (centre > tolerance || radius > tolerance) return null;
      return Math.max(centre, radius);
    }

    case 'ARC': {
      const t = truth as typeof produced;
      const centre = dist(produced.centre, t.centre);
      const radius = Math.abs(produced.radius - t.radius);
      if (centre > tolerance || radius > tolerance) return null;
      if (angleGap(produced.startAngle, t.startAngle) > angleToleranceDeg) return null;
      if (angleGap(produced.endAngle, t.endAngle) > angleToleranceDeg) return null;
      return Math.max(centre, radius);
    }

    case 'TEXT':
    case 'MTEXT': {
      const t = truth as typeof produced;
      if (normaliseForComparison(produced.text) !== normaliseForComparison(t.text)) return null;
      const anchor = dist(produced.anchor, t.anchor);
      if (anchor > tolerance) return null;
      /*
        Size and angle, which for a long time nothing compared.

        A text comparison that reads the string and the anchor is blind to the
        shape of what it is comparing, and the cost of that was not theoretical:
        DXF group 40 is a CAP height while the contract's `height` is the font's
        em, the writer handed one to the other, and a reader therefore drew every
        string in every export `1 / 0.716 = 1.40` times too tall while all three
        stages reported text at 1.000. The anchor was exact, the string was
        exact, so the match was perfect.

        `height` is a length in drawing millimetres, so it is held to the board's
        length tolerance T -- the same T as the anchor, because it is the same
        kind of quantity. `rotation` is an angle, so it takes the angular
        tolerance the board fixed for arcs, for the same reason.
      */
      const height = Math.abs(produced.height - t.height);
      if (height > tolerance) return null;
      if (angleGap(produced.rotation, t.rotation) > angleToleranceDeg) return null;
      /*
        And the pitch, where both sides state one.

        A block's anchor is its FIRST baseline, so every line after the first can
        be anywhere at all and the match still holds -- which is how a 14 % pitch
        error on four rotated fixtures went unseen.

        Both sides state one for the vector corpus: ground truth declares the
        spacing the generator drew with, and the DXF read-back recovers it from
        group 44. The comparison corpus declares none, and asserting nothing there
        is the point of the condition rather than a way around it -- a truth file
        that has not annotated a pitch has not made a claim about one, and
        inventing a number to compare against is what `height: 1` and
        `height: 3.5` both were.

        Gate only, not part of the returned cost: the cost ranks candidates for
        assignment, and the anchor and the height already order them.
      */
      const wantPitch = t.linePitch;
      const gotPitch = produced.linePitch;
      if (wantPitch !== undefined && gotPitch !== undefined) {
        if (Math.abs(gotPitch - wantPitch) > tolerance) return null;
      }
      return Math.max(anchor, height);
    }
  }
}

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Smallest absolute difference between two angles, in degrees. */
export function angleGap(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360 + 360) % 360);
  return Math.min(d, 360 - d);
}

/** Beyond this many vertices, only forward and reverse traversal are tried. */
const MAX_CYCLIC_VERTICES = 64;

/** A way of walking the same shape: the vertices in an order, with their bulges. */
interface Walk {
  v: readonly Point[];
  b: readonly number[];
}

/**
 * The same shape walked backwards.
 *
 * The bulges reverse **and negate**, and they shift by one place. A bulge belongs
 * to the segment *leaving* its vertex, so walking backwards means the segment
 * that left vertex `i` now arrives at it, curving the other way: `b'[j]` is
 * `-b[n - 2 - j]`. Reversing the vertices and not the bulges rounds every corner
 * the wrong way on any drawing a producer happened to write in the other
 * direction — and every vertex still agrees, so nothing else would notice.
 */
function reverseWalk(w: Walk): Walk {
  const n = w.v.length;
  return {
    v: [...w.v].reverse(),
    b: Array.from({ length: n }, (_, j) => -(w.b[(((n - 2 - j) % n) + n) % n] ?? 0)),
  };
}

/** Rotated start vertex. The bulges rotate by the same amount, being per segment. */
function rotateWalk(w: Walk, by: number): Walk {
  const n = w.v.length;
  return {
    v: Array.from({ length: n }, (_, j) => w.v[(j + by) % n]!),
    b: Array.from({ length: n }, (_, j) => w.b[(j + by) % n] ?? 0),
  };
}

/**
 * The cost of one traversal, or null when it does not describe the same shape.
 *
 * Both conditions are in the board's units: the vertices have to agree within T,
 * and the bulges have to agree closely enough that the arc's apex sits within T
 * of where it should. The second is a distance rather than a dimensionless bulge
 * difference, which is what keeps one tolerance doing both jobs.
 */
function walkCost(
  pv: readonly Point[],
  pb: readonly number[],
  w: Walk,
  closed: boolean,
  tolerance: number,
): number | null {
  const n = pv.length;
  let worst = 0;
  for (let i = 0; i < n; i++) worst = Math.max(worst, dist(pv[i]!, w.v[i]!));
  if (worst > tolerance) return null;

  // An open polyline's last vertex has no segment leaving it, so its bulge slot
  // means nothing to either side and comparing it compares junk.
  const segments = closed ? n : n - 1;
  for (let i = 0; i < segments; i++) {
    const displaced = bulgeDisplacement(pv[i]!, pv[(i + 1) % n]!, pb[i] ?? 0, w.b[i] ?? 0);
    if (displaced > tolerance) return null;
    worst = Math.max(worst, displaced);
  }
  return worst;
}

/**
 * The cheapest traversal that describes the same shape, or null when none does:
 * both directions always, and for a closed polyline every starting vertex as
 * well.
 */
function bestTraversal(
  produced: { vertices: readonly Point[]; bulges: readonly number[]; closed: boolean },
  truth: { vertices: readonly Point[]; bulges: readonly number[] },
  tolerance: number,
): number | null {
  const pv = produced.vertices;
  const pb = produced.bulges;
  const closed = produced.closed;
  const forward: Walk = { v: truth.vertices, b: truth.bulges };
  const backward = reverseWalk(forward);

  const candidates: Walk[] = [forward, backward];
  if (closed && pv.length <= MAX_CYCLIC_VERTICES) {
    for (let shift = 1; shift < pv.length; shift++) {
      candidates.push(rotateWalk(forward, shift), rotateWalk(backward, shift));
    }
  }

  let best: number | null = null;
  for (const w of candidates) {
    const cost = walkCost(pv, pb, w, closed, tolerance);
    if (cost === null) continue;
    if (best === null || cost < best) best = cost;
    if (best === 0) break;
  }
  return best;
}

